import {
  Button,
  ChakraCheckbox,
  ChakraCheckboxGroup,
  ChakraFlex,
  ChakraText,
  ChakraToolTip,
  CircleInfo,
  Edit,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';

import { capitalizeFirstLetter } from '~/lib/common';
import { UserType } from '~/lib/constants/user.constants';
import { getPreferredFullName } from '~/lib/util/patient.util';

const { label, labelBold, paragraph } = EquipUITheme.typography;
const { neutral, primary } = EquipUIFireflyV2Theme.colors;
const TOP_LEVEL_ID = 'add-notes-form';

interface EditNoteFormAttendeeListProps {
  attendees: GroupClassEventAttendee[];
  declinedUsers?: GroupClassEventAttendee[];
  hasEventEnded?: boolean;
  isEditAttendeesClicked: boolean;
  onAttendeeSelection: (userId: string, attended: boolean) => void;
  onEditAttendeesClicked: (isClicked: boolean) => void;
  selectedAttendees: AttendeeItem[];
}

export const EditNoteFormAttendeeList = ({
  attendees,
  declinedUsers,
  hasEventEnded,
  isEditAttendeesClicked,
  onAttendeeSelection,
  onEditAttendeesClicked,
  selectedAttendees,
}: EditNoteFormAttendeeListProps) => {
  // TODO should this be its own component?
  const renderAttendee = (
    {
      chosenName,
      externalId,
      firstName,
      lastName,
      supportPatientLinks,
      type,
      attended,
    }: GroupClassEventAttendee,
    declined = false,
  ) => {
    const fullName = getPreferredFullName(chosenName, firstName, lastName);
    const typeText = capitalizeFirstLetter(type);

    let supportText = '';
    const textDecoration =
      hasEventEnded && (declined || attended === false) ? 'line-through' : '';
    if (type === UserType.SUPPORT && supportPatientLinks?.length > 0) {
      supportText = 'for ';
      supportText += supportPatientLinks
        ?.map(({ patientFirstName, patientLastName }, index) => {
          const patientFullName = getPreferredFullName(
            '',
            patientFirstName,
            patientLastName,
          );
          if (index === 0) {
            return patientFullName;
          }
          if (index === supportPatientLinks.length - 1) {
            return ` & ${patientFullName}`;
          }
          return ` , ${patientFullName}`;
        })
        .join('');
    }

    const isAttendeeSelected = selectedAttendees.some(
      (selectedAttendee) =>
        selectedAttendee.userId === externalId && selectedAttendee.attended,
    );

    const fullNameElement = (
      <ChakraText
        {...paragraph}
        color="neutral.primary"
        id={`${TOP_LEVEL_ID}__attendee-full-name${externalId}`}
        textDecoration={textDecoration}
      >
        {fullName}
      </ChakraText>
    );
    return (
      <ChakraFlex
        alignItems="center"
        id={`${TOP_LEVEL_ID}__attendee-${externalId}`}
        key={externalId}
      >
        {isEditAttendeesClicked ? (
          <ChakraCheckbox
            colorScheme="teal"
            isChecked={isAttendeeSelected}
            onChange={(e) => onAttendeeSelection(externalId, e.target.checked)}
          >
            {fullNameElement}
          </ChakraCheckbox>
        ) : (
          fullNameElement
        )}
        <ChakraText
          {...label}
          color="neutral.secondary"
          marginLeft="4px"
          marginTop="2px"
          textDecoration={textDecoration}
        >
          {typeText}
        </ChakraText>
        {type === UserType.SUPPORT && (
          <ChakraText
            {...label}
            color="neutral.secondary"
            marginLeft="4px"
            marginTop="2px"
            textDecoration={textDecoration}
          >
            {supportText}
          </ChakraText>
        )}
      </ChakraFlex>
    );
  };

  return (
    <ChakraFlex
      alignSelf="stretch"
      flexDirection="column"
      id={`${TOP_LEVEL_ID}__attendees-container`}
      justifyContent="center"
    >
      <ChakraFlex justifyContent="space-between">
        <ChakraFlex alignItems="center">
          <ChakraText
            {...label}
            color="neutral.secondary"
            id={`${TOP_LEVEL_ID}__attendees-label`}
            marginRight="8px"
          >
            Attendees
          </ChakraText>
          <ChakraToolTip
            bg="white"
            border={`1px solid ${neutral.line}`}
            borderRadius="8px"
            color="neutral.primary"
            hasArrow
            label="This list includes all registrants for this class, excluding those who declined. Please review carefully and edit the list if necessary."
            padding="8px"
            shouldWrapChildren
          >
            <CircleInfo height="16px" width="16px" />
          </ChakraToolTip>
        </ChakraFlex>
        <Button
          id={`${TOP_LEVEL_ID}__edit-attendees`}
          isDisabled={isEditAttendeesClicked || !hasEventEnded}
          onClick={() => onEditAttendeesClicked(true)}
          rightIcon={<Edit fill={primary[100]} height="12px" width="12px" />}
          size="xs"
          variant="transparent"
        >
          <ChakraText
            {...labelBold}
            color="primary.100"
            id={`${TOP_LEVEL_ID}__edit-attendees`}
            lineHeight="16.8px"
          >
            Edit attendees
          </ChakraText>
        </Button>
      </ChakraFlex>
      <ChakraFlex
        alignItems="flex-start"
        alignSelf="stretch"
        border={`1px solid ${neutral.background.secondary}`}
        borderRadius="16px"
        flexDirection="column"
        gap="12px"
        marginBottom="16px"
        marginTop="8px"
        maxHeight="160px"
        overflow="auto"
        padding="12px"
      >
        <ChakraCheckboxGroup colorScheme="teal">
          {attendees?.map((attendee) => renderAttendee(attendee))}
          {declinedUsers?.map((declinedUser) =>
            renderAttendee(declinedUser, true),
          )}
        </ChakraCheckboxGroup>
      </ChakraFlex>
    </ChakraFlex>
  );
};
